import { AspectRatio, Box, BoxProps, Container, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'

import { getBrand } from '@lib/util/brand-handler'
import BrandLayout from '@layouts/brand-layout'
import Head from '@modules/common/components/head'
import Hero from './hero'

import Elimination from './elimination'
import Effectiveness from './effectiveness'
import Optimizing from './optimizing'
import Ingredient from './ingredient'
import Recommendation from './recommendation'
import KeyIngredient from './key-ingredient'
import HowToUse from './how-to-use'
import Review from './review'
import Deals from './deals'
import QA from './qa'

import EclusiveOffer from './exclusive-offer'
import useAosAnimation from '@common/hooks/use-aos-animation'

export const CONTAINER_WIDTH = {
  LARGE: 990,
  MEDIUM: 760,
  SMALL: 600
}

interface BgWrapperProps {
  url: string
  children?: React.ReactNode
}

const BgWrapper = ({ url, children, ...props }: BgWrapperProps & BoxProps) => {
  return (
    <Box
      bgImage={`url(${url})`}
      w="100%"
      bgSize="cover"
      bgPosition="center"
      {...props}
    >
      {children}
    </Box>
  )
}

export const MainContainer = (props: BoxProps) => {
  return (
    <Container
      maxW={{
        base: CONTAINER_WIDTH.SMALL,
        md: CONTAINER_WIDTH.MEDIUM,
        lg: CONTAINER_WIDTH.LARGE
      }}
      {...props}
    />
  )
}

const Oralcare = () => {
  const { data } = useQuery(['brand', 'healthplus'], () =>
    getBrand('healthplus')
  )

  useAosAnimation({
    duration: 600,
    once: false
  })

  if (!data) return null

  const { title, metadata, productSection, color } = data

  return (
    <>
      <Head title={title} description={metadata.meta_descrtiption} />
      <BrandLayout
        name={title}
        color={color || '#E31C5A'}
        brand={data}
        productSection={productSection}
      >
        <VStack w="100%" alignItems="center" gap={0}>
          <Hero />
          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-eliminate-1714039909365.png'
            }
            position="relative"
            h={{
              base: '177px',
              sm: '300px',
              md: '400px'
            }}
          >
            <MainContainer data-aos="fade-up">
              <Elimination />
            </MainContainer>

            <VStack data-aos="fade-up" top={0} left={0} position="absolute">
              <AspectRatio
                w={{
                  base: '60px',
                  sm: '100px',
                  md: '160px',
                  lg: '200px',
                  xl: '200px'
                }}
                ratio={981 / 864}
                position="relative"
                zIndex={10}
              >
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/leaf-top-1714039909366.png'
                  }
                  alt="leaf top"
                  fill
                />
              </AspectRatio>
            </VStack>

            <VStack data-aos="fade-up" bottom={0} left={0} position="absolute">
              <AspectRatio
                w={{
                  base: '60px',
                  sm: '100px',
                  md: '160px',
                  lg: '200px',
                  xl: '200px'
                }}
                ratio={1274 / 864}
                position="relative"
                zIndex={10}
              >
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/leaf-bottom-1714039909366.png'
                  }
                  alt="leaf bottom"
                  fill
                />
              </AspectRatio>
            </VStack>

            <VStack data-aos="fade-up" top={0} right={0} position="absolute">
              <AspectRatio
                w={{
                  base: '120px',
                  sm: '203px',
                  md: '271px'
                }}
                ratio={585 / 864}
                position="relative"
                zIndex={10}
                mb={4}
              >
                <Image
                  src={
                    'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/smile-1714039909367.png'
                  }
                  alt="smile"
                  fill
                />
              </AspectRatio>
            </VStack>
          </BgWrapper>
          <MainContainer bgColor="#fefefa">
            <EclusiveOffer />
          </MainContainer>
          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-wrapper-1714039909367.png'
            }
          >
            <MainContainer>
              <Effectiveness />
            </MainContainer>
          </BgWrapper>

          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-eliminate-1714039909365.png'
            }
            position="relative"
            h={{
              base: '210px',
              sm: '240px'
            }}
          >
            <MainContainer>
              <Optimizing />
            </MainContainer>
          </BgWrapper>

          <MainContainer bgColor="#fefefa">
            <Recommendation />
            <KeyIngredient />
          </MainContainer>

          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-eliminate-1714039909365.png'
            }
            position="relative"
            h={{
              base: '120px',
              sm: '160px'
            }}
          >
            <MainContainer>
              <Ingredient />
            </MainContainer>
          </BgWrapper>

          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-wrapper-1714039909367.png'
            }
          >
            <MainContainer>
              <HowToUse />
              <Review />
            </MainContainer>
          </BgWrapper>

          <BgWrapper
            data-aos="fade-up"
            url={
              'https://habbit-sg.s3.ap-southeast-1.amazonaws.com/bg-deals-1714039909368.png'
            }
          >
            <MainContainer>
              <Deals />
            </MainContainer>
          </BgWrapper>

          <MainContainer bgColor="#fefefa">
            <QA />
          </MainContainer>
        </VStack>
      </BrandLayout>
    </>
  )
}

export default Oralcare
